import { UserPlus, UserMinus, Lock, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import type { PlayerCardData } from './PlayerCardMobile';

export interface AddDropApi {
  rosterLocked: boolean;
  myTeam: { id: string } | null | undefined;
  isAcquisitionLimitReached: boolean;
  isFreeAgent: (id: string) => boolean;
  isOnMyTeam: (id: string) => boolean;
  isOnWaiverWire: (id: string) => boolean;
  getOwnerTeamName: (id: string) => string | null;
}

interface PlayerActionMobileProps {
  player: PlayerCardData;
  addDrop: AddDropApi;
  waiverMode: WaiverMode;
  onAddClick: (p: PlayerCardData) => void;
  onDropClick: (id: string) => void;
  onTradeClick: () => void;
}

// Conditional ordering mirrors desktop MLBBetaPlayers.tsx:498-551 byte-for-byte.
// Visual sizing adapts to the mobile spec (32×32 icon-only); the responsive
// label-hide trick from desktop doesn't apply on a mobile-only surface.
export function PlayerActionMobile({
  player, addDrop, waiverMode, onAddClick, onDropClick, onTradeClick,
}: PlayerActionMobileProps) {
  if (addDrop.rosterLocked) return <Lock className="w-4 h-4 text-muted-foreground" />;
  if (!addDrop.myTeam) return null;
  if (addDrop.isAcquisitionLimitReached && addDrop.isFreeAgent(player.id)) {
    return <Lock className="w-4 h-4 text-muted-foreground" />;
  }
  if (addDrop.isFreeAgent(player.id)) {
    const isClaim = addDrop.isOnWaiverWire(player.id) && (waiverMode === 'faab_24' || waiverMode === 'faab_48');
    return (
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-primary hover:text-primary"
        aria-label={isClaim ? 'Claim player' : 'Add player'}
        title={isClaim ? 'Claim' : 'Add'}
        onClick={(e) => { e.stopPropagation(); onAddClick(player); }}
      >
        <UserPlus className="w-4 h-4" />
      </Button>
    );
  }
  if (addDrop.isOnMyTeam(player.id)) {
    return (
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-destructive hover:text-destructive"
        aria-label="Drop player"
        title="Drop"
        onClick={(e) => { e.stopPropagation(); onDropClick(player.id); }}
      >
        <UserMinus className="w-4 h-4" />
      </Button>
    );
  }
  const ownerName = addDrop.getOwnerTeamName(player.id);
  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-8 w-8 text-primary hover:text-primary"
      aria-label={`Trade with ${ownerName ?? 'owner'}`}
      title={`Trade with ${ownerName ?? 'owner'}`}
      onClick={(e) => { e.stopPropagation(); onTradeClick(); }}
    >
      <ArrowLeftRight className="w-4 h-4" />
    </Button>
  );
}

export default PlayerActionMobile;
